/**
 * SettlementTable Component
 * 라이더 주정산내역 테이블 영역
 * - 컬럼 고정 (NO, 라이더ID, 라이더명)
 * - 정렬 기능 (부모 컴포넌트에서 관리)
 * - 검색 결과 건수 표시
 * - 라이더 상세 모달
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RiderSettlement } from '../types/riderSettlement.types';
import {
    TABLE_COLUMNS,
    TableColumn,
    shouldShowColumn,
    formatCurrency,
    getColumnAlignment
} from '../lib/riderSettlementHelpers';
import RiderDetailModal from './RiderDetailModal';
import '../styles/RiderSettlementHistory.css';

// ========================================
// 헤더 툴팁 컴포넌트 (Portal 사용)
// ========================================
interface HeaderTooltipProps {
    content: string;
    targetRef: React.RefObject<HTMLElement | null>;
    visible: boolean;
}

const HeaderTooltip: React.FC<HeaderTooltipProps> = ({ content, targetRef, visible }) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (visible && targetRef.current) {
            const rect = targetRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + 8,
                left: rect.left + rect.width / 2
            });
        }
    }, [visible, targetRef]);

    if (!visible) return null;

    return ReactDOM.createPortal(
        <div
            className="rider-settlement-header-tooltip"
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                transform: 'translateX(-50%)',
                zIndex: 9999
            }}
        >
            <div className="tooltip-arrow"></div>
            <div className="tooltip-title">계산식</div>
            <div className="tooltip-content">
                {content.split('\n').map((line, idx) => (
                    <p key={idx}>{line}</p>
                ))}
            </div>
        </div>,
        document.body
    );
};

type SortDirection = 'asc' | 'desc' | null;

interface SortConfig {
    columnIndex: number | null;
    direction: SortDirection;
}

interface SettlementTableProps {
    settlements: RiderSettlement[];
    currentItems: RiderSettlement[];
    currentPage: number;
    loading: boolean;
    isRiderUser: boolean;
    sortConfig: SortConfig;
    onSort: (columnIndex: number) => void;
}

const SettlementTable: React.FC<SettlementTableProps> = ({
    settlements,
    currentItems,
    currentPage,
    loading,
    isRiderUser,
    sortConfig,
    onSort
}) => {
    // 상세 모달 상태
    const [selectedSettlement, setSelectedSettlement] = useState<RiderSettlement | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // 툴팁 상태
    const [activeTooltip, setActiveTooltip] = useState<number | null>(null);
    const headerRefs = useRef<{ [key: number]: HTMLTableCellElement | null }>({});

    // 스크롤 동기화를 위한 ref
    const fixedBodyRef = useRef<HTMLTableSectionElement>(null);
    const scrollableBodyRef = useRef<HTMLTableSectionElement>(null);
    const scrollableContainerRef = useRef<HTMLDivElement>(null);

    // 고정 컬럼 인덱스 (NO, 라이더ID, 라이더명)
    const FIXED_COLUMN_INDICES = [1, 2, 3];

    /**
     * 표시할 컬럼 필터링
     */
    const visibleColumns = TABLE_COLUMNS.filter(col => shouldShowColumn(col.index, isRiderUser));

    // 고정 컬럼과 스크롤 가능 컬럼 분리
    const fixedColumns = visibleColumns.filter(col => FIXED_COLUMN_INDICES.includes(col.index));
    const scrollableColumns = visibleColumns.filter(col => !FIXED_COLUMN_INDICES.includes(col.index));

    /**
     * 행 클릭 핸들러 (상세 모달 열기)
     */
    const handleRowClick = useCallback((settlement: RiderSettlement) => {
        setSelectedSettlement(settlement);
        setShowDetailModal(true);
    }, []);

    /**
     * 모달 닫기
     */
    const handleCloseModal = useCallback(() => {
        setShowDetailModal(false);
        setSelectedSettlement(null);
    }, []);

    /**
     * 스크롤 동기화
     */
    useEffect(() => {
        const scrollableContainer = scrollableContainerRef.current;
        const fixedBody = fixedBodyRef.current;
        const scrollableBody = scrollableBodyRef.current;

        if (!scrollableContainer || !fixedBody || !scrollableBody) return;

        const handleScroll = () => {
            // 수직 스크롤 동기화
            if (fixedBody.parentElement) {
                fixedBody.parentElement.scrollTop = scrollableContainer.scrollTop;
            }
        };

        scrollableContainer.addEventListener('scroll', handleScroll);
        return () => scrollableContainer.removeEventListener('scroll', handleScroll);
    }, []);

    /**
     * 정렬 아이콘 렌더링
     */
    const renderSortIcon = (columnIndex: number) => {
        const isSorted = sortConfig.columnIndex === columnIndex;
        const direction = isSorted ? sortConfig.direction : null;

        return (
            <span className="sort-icon">
                {direction === 'asc' && <i className="fas fa-sort-up"></i>}
                {direction === 'desc' && <i className="fas fa-sort-down"></i>}
                {!direction && <i className="fas fa-sort" style={{ opacity: 0.3 }}></i>}
            </span>
        );
    };

    /**
     * 셀 값 렌더링
     */
    const renderCellValue = (
        settlement: RiderSettlement,
        columnIndex: number,
        rowIndex: number
    ): React.ReactNode => {
        switch (columnIndex) {
            case 1:
                return (currentPage - 1) * 10 + rowIndex + 1;
            case 2:
                return settlement.riderId;
            case 3:
                return settlement.riderName;
            case 4:
                return settlement.riderType;
            case 5:
                return settlement.settlementStartDate;
            case 6:
                return settlement.settlementEndDate;
            case 7:
                return formatCurrency(settlement.processCount);
            case 8:
                return formatCurrency(settlement.deliveryFee);
            case 9:
                return formatCurrency(settlement.additionalPayment);
            case 10:
                return formatCurrency(settlement.brokerManageFee);
            case 11:
                return formatCurrency(settlement.partnerOperationFee);
            case 12:
                return formatCurrency(settlement.totalOperationFee);
            case 13:
                return formatCurrency(settlement.taxableDeliveryFee);
            case 14:
                return formatCurrency(settlement.monthlySalary);
            case 15:
                return formatCurrency(settlement.riderEmployment);
            case 16:
                return formatCurrency(settlement.riderIndustrial);
            case 17:
                return formatCurrency(settlement.hourlyInsurance);
            case 18:
                return formatCurrency(settlement.insuranceTotal);
            case 19:
                return formatCurrency(settlement.promotion);
            case 20:
                return formatCurrency(settlement.incomeTax);
            case 21:
                return formatCurrency(settlement.localTax);
            case 22:
                return formatCurrency(settlement.withholdingTax);
            case 23:
                return formatCurrency(settlement.finalDeliveryFee);
            case 24:
                return formatCurrency(settlement.advanceSettlement);
            case 25:
                return formatCurrency(settlement.dailyDeduction);
            case 26:
                return formatCurrency(settlement.actualPayment);
            default:
                return '-';
        }
    };

    /**
     * 셀 클래스 및 스타일 반환
     */
    const getCellProps = (columnIndex: number): { className?: string; style?: React.CSSProperties } => {
        const alignment = getColumnAlignment(columnIndex);
        const isBold = columnIndex === 26; // 실지급배달료 컬럼

        if (alignment.textAlign === 'center') {
            return { className: 'text-center' };
        }
        if (alignment.textAlign === 'right') {
            return { style: { textAlign: 'right', fontWeight: isBold ? 'bold' : undefined } };
        }
        return {};
    };

    return (
        <>
            {/* 검색 결과 건수 */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                검색 결과: <strong>{settlements.length.toLocaleString()}건</strong>
            </div>

            <div className="rider-settlement-table-container">
                <div className="rider-settlement-table-wrapper">
                    {/* 고정 컬럼 영역 */}
                    <div className="fixed-columns">
                        <table className="rider-settlement-table">
                            <thead>
                                <tr>
                                    {fixedColumns.map(column => {
                                        const hasTooltip = !!column.tooltip;
                                        return (
                                            <th
                                                key={column.index}
                                                ref={el => { headerRefs.current[column.index] = el; }}
                                                style={{ width: column.width, minWidth: column.minWidth }}
                                                className={`sortable ${sortConfig.columnIndex === column.index ? 'sorted' : ''} ${hasTooltip ? 'has-tooltip' : ''}`}
                                                onClick={() => onSort(column.index)}
                                                onMouseEnter={() => hasTooltip && setActiveTooltip(column.index)}
                                                onMouseLeave={() => setActiveTooltip(null)}
                                            >
                                                <span className="header-content">
                                                    {column.header}
                                                    {hasTooltip && <i className="fas fa-info-circle tooltip-icon"></i>}
                                                </span>
                                                {renderSortIcon(column.index)}
                                                {hasTooltip && column.tooltip && (
                                                    <HeaderTooltip
                                                        content={column.tooltip}
                                                        targetRef={{ current: headerRefs.current[column.index] }}
                                                        visible={activeTooltip === column.index}
                                                    />
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody ref={fixedBodyRef}>
                                {!loading && settlements.length === 0 ? (
                                    <tr>
                                        <td colSpan={fixedColumns.length} className="text-center text-gray-600">
                                            {/* 빈 공간 - 스크롤 영역에서 메시지 표시 */}
                                        </td>
                                    </tr>
                                ) : (
                                    currentItems.map((settlement, index) => (
                                        <tr
                                            key={settlement.no}
                                            onClick={() => handleRowClick(settlement)}
                                        >
                                            {fixedColumns.map(column => (
                                                <td key={column.index} {...getCellProps(column.index)}>
                                                    {renderCellValue(settlement, column.index, index)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 스크롤 가능 컬럼 영역 */}
                    <div className="scrollable-columns" ref={scrollableContainerRef}>
                        <table className="rider-settlement-table">
                            <thead>
                                <tr>
                                    {scrollableColumns.map(column => {
                                        const hasTooltip = !!column.tooltip;
                                        return (
                                            <th
                                                key={column.index}
                                                ref={el => { headerRefs.current[column.index] = el; }}
                                                style={{ width: column.width, minWidth: column.minWidth }}
                                                className={`sortable ${sortConfig.columnIndex === column.index ? 'sorted' : ''} ${hasTooltip ? 'has-tooltip' : ''}`}
                                                onClick={() => onSort(column.index)}
                                                onMouseEnter={() => hasTooltip && setActiveTooltip(column.index)}
                                                onMouseLeave={() => setActiveTooltip(null)}
                                            >
                                                <span className="header-content">
                                                    {column.header}
                                                    {hasTooltip && <i className="fas fa-info-circle tooltip-icon"></i>}
                                                </span>
                                                {renderSortIcon(column.index)}
                                                {hasTooltip && column.tooltip && (
                                                    <HeaderTooltip
                                                        content={column.tooltip}
                                                        targetRef={{ current: headerRefs.current[column.index] }}
                                                        visible={activeTooltip === column.index}
                                                    />
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody ref={scrollableBodyRef}>
                                {!loading && settlements.length === 0 ? (
                                    <tr>
                                        <td colSpan={scrollableColumns.length} className="text-center text-gray-600">
                                            조회된 정산내역이 없습니다.
                                        </td>
                                    </tr>
                                ) : (
                                    currentItems.map((settlement, index) => (
                                        <tr
                                            key={settlement.no}
                                            onClick={() => handleRowClick(settlement)}
                                        >
                                            {scrollableColumns.map(column => (
                                                <td key={column.index} {...getCellProps(column.index)}>
                                                    {renderCellValue(settlement, column.index, index)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* 라이더 상세 모달 */}
            {showDetailModal && selectedSettlement && (
                <RiderDetailModal
                    settlement={selectedSettlement}
                    onClose={handleCloseModal}
                    isRiderUser={isRiderUser}
                />
            )}
        </>
    );
};

export default SettlementTable;
